import React, { useEffect, useRef, useState } from 'react';
import NavHeader from '../components/NavHeader';
import { fetchSettings, updateSettings, recomputeVatThreshold } from '../api';

const TOKENS = {
  bg: 'oklch(0.985 0.003 250)',
  panel: 'oklch(1 0 0)',
  panelAlt: 'oklch(0.975 0.004 250)',
  border: 'oklch(0.92 0.006 250)',
  borderStrong: 'oklch(0.86 0.008 250)',
  ink: 'oklch(0.18 0.012 260)',
  ink2: 'oklch(0.42 0.012 260)',
  ink3: 'oklch(0.62 0.010 255)',
  accent: 'oklch(0.55 0.15 260)',
  ok: 'oklch(0.62 0.13 150)',
  danger: 'oklch(0.58 0.18 25)',
};
// Repointed to the shared Inter stack (src/typography.css).
const UI_FONT = 'var(--font-ui)';

// Spreadable numeric style: inline styles win over CSS classes, so numeric
// inputs/amounts spread this instead of the .num utility class.
const NUM_STYLE = {
  fontFamily: 'var(--font-ui)',
  fontVariantNumeric: 'tabular-nums',
};

// Setting keys — must match SettingKey in src/invoice/application/app_settings.py
const KEYS = {
  AUTO_ROUTE_ENABLED: 'auto_pending_approval_enabled',
  AUTO_ROUTE_THRESHOLD: 'auto_pending_approval_confidence_threshold',
  ASSIGNMENT_THRESHOLD: 'classification_assignment_threshold',
  SHOW_EXTRACTED_TEXT: 'show_extracted_text',
  VAT_THRESHOLD: 'vat_threshold_amount',
  VAT_PER_INVOICE_PACKAGES: 'vat_per_invoice_packages_enabled',
};

function Section({ title, children }) {
  return (
    <section style={{ marginBottom: 24 }}>
      <h3
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: TOKENS.ink3,
          margin: '0 0 10px',
        }}
      >
        {title}
      </h3>
      <div
        style={{
          background: TOKENS.panel,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: 10,
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {children}
      </div>
    </section>
  );
}

function Field({ label, helper, children, disabled }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: '4px 16px',
        alignItems: 'center',
        opacity: disabled ? 0.5 : 1,
        transition: 'opacity .12s',
      }}
    >
      <label
        style={{
          fontSize: 13,
          fontWeight: 550,
          color: TOKENS.ink,
        }}
      >
        {label}
      </label>
      <div>{children}</div>
      {helper && (
        <span
          style={{
            gridColumn: '1 / -1',
            fontSize: 11.5,
            color: TOKENS.ink3,
            lineHeight: 1.4,
          }}
        >
          {helper}
        </span>
      )}
    </div>
  );
}

function NumberInput({ value, onChange, disabled, step = 0.05 }) {
  return (
    <input
      type="number"
      min="0"
      max="1"
      step={step}
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const parsed = parseFloat(e.target.value);
        onChange(Number.isFinite(parsed) ? parsed : 0);
      }}
      style={{
        width: 80,
        padding: '5px 8px',
        borderRadius: 6,
        border: `1px solid ${TOKENS.borderStrong}`,
        background: disabled ? TOKENS.panelAlt : TOKENS.panel,
        ...NUM_STYLE,
        fontSize: 13,
        textAlign: 'right',
        color: TOKENS.ink,
      }}
    />
  );
}

// Money amount input — min 0, no upper bound, whole euros. Distinct from
// NumberInput, which is hard-clamped to [0,1] for confidence ratios.
function MoneyInput({ value, onChange, disabled, step = 1 }) {
  return (
    <input
      type="number"
      min="0"
      step={step}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => {
        const parsed = parseFloat(e.target.value);
        // Whole euros only — the backend rejects fractional thresholds.
        const next = Number.isFinite(parsed) ? Math.max(0, Math.round(parsed)) : 0;
        onChange(next);
      }}
      style={{
        width: 120,
        padding: '5px 8px',
        borderRadius: 6,
        border: `1px solid ${TOKENS.borderStrong}`,
        background: disabled ? TOKENS.panelAlt : TOKENS.panel,
        ...NUM_STYLE,
        fontSize: 13,
        textAlign: 'right',
        color: TOKENS.ink,
      }}
    />
  );
}

function Checkbox({ checked, onChange, disabled }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      style={{ width: 16, height: 16, cursor: disabled ? 'not-allowed' : 'pointer' }}
    />
  );
}

function fmtEur(n) {
  const v = Number.isFinite(Number(n)) ? Number(n) : 0;
  return v.toLocaleString('en-IE', { maximumFractionDigits: 2 });
}

function VatThresholdModal({ oldValue, newValue, busy, onApply, onForward, onCancel }) {
  const btnBase = {
    padding: '8px 14px',
    borderRadius: 6,
    fontFamily: UI_FONT,
    fontSize: 13,
    fontWeight: 600,
    cursor: busy ? 'wait' : 'pointer',
  };
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'oklch(0.18 0.012 260 / 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 1000,
      }}
      onClick={busy ? undefined : onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: TOKENS.panel,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: 12,
          padding: '20px 22px',
          width: '100%',
          maxWidth: 460,
          boxShadow: '0 12px 40px oklch(0.18 0.012 260 / 0.25)',
          fontFamily: UI_FONT,
          color: TOKENS.ink,
        }}
      >
        <h2
          style={{
            fontSize: 16,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            margin: '0 0 10px',
          }}
        >
          Apply VAT threshold change?
        </h2>
        <p style={{ fontSize: 13, lineHeight: 1.55, color: TOKENS.ink2, margin: '0 0 10px' }}>
          The VAT threshold changed from{' '}
          <strong style={{ ...NUM_STYLE, color: TOKENS.ink }}>€{fmtEur(oldValue)}</strong>{' '}
          to{' '}
          <strong style={{ ...NUM_STYLE, color: TOKENS.ink }}>€{fmtEur(newValue)}</strong>.
          Apply it retroactively to invoices that are{' '}
          <strong style={{ color: TOKENS.ink }}>not yet approved</strong> (received, under review,
          or pending approval)?
        </p>
        <p
          style={{
            fontSize: 11.5,
            lineHeight: 1.5,
            color: TOKENS.ink3,
            margin: '0 0 18px',
            padding: '8px 10px',
            background: TOKENS.panelAlt,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: 8,
          }}
        >
          Invoices that are already approved, declined, or further along — submitted to AP, paid, or
          in a VAT/Luxembourg batch — will not be affected.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              ...btnBase,
              background: TOKENS.panel,
              color: TOKENS.ink2,
              border: `1px solid ${TOKENS.borderStrong}`,
              opacity: busy ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={onForward}
            disabled={busy}
            style={{
              ...btnBase,
              background: TOKENS.panelAlt,
              color: TOKENS.ink,
              border: `1px solid ${TOKENS.borderStrong}`,
              opacity: busy ? 0.6 : 1,
            }}
          >
            Only going forward
          </button>
          <button
            onClick={onApply}
            disabled={busy}
            style={{
              ...btnBase,
              background: TOKENS.accent,
              color: '#fff',
              border: 'none',
              opacity: busy ? 0.7 : 1,
            }}
          >
            {busy ? 'Saving…' : 'Apply to pending invoices'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [vatModalOpen, setVatModalOpen] = useState(false);
  // Threshold value as last persisted by the server — used to decide whether
  // the VAT threshold actually changed before prompting on save.
  const initialVatThreshold = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetchSettings()
      .then((res) => {
        if (!cancelled) {
          setSettings(res.data);
          initialVatThreshold.current = res.data?.[KEYS.VAT_THRESHOLD];
        }
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err.response?.data?.detail || err.message || 'Failed to load settings';
        setToast({ type: 'error', text: msg });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (key, value) => {
    setSettings((s) => ({ ...s, [key]: value }));
  };

  const vatThresholdChanged = () => {
    // Normalize absent/non-numeric to null so an unset key never reads as a
    // change. NaN !== NaN would otherwise spuriously open the modal.
    const norm = (v) => (Number.isFinite(Number(v)) ? Number(v) : null);
    return norm(settings?.[KEYS.VAT_THRESHOLD]) !== norm(initialVatThreshold.current);
  };

  // Persist settings. Returns the saved settings on success, or null on error
  // (toast already set). Resets the tracked initial threshold so an immediate
  // re-save without another change does not re-prompt.
  const persistSettings = async () => {
    const res = await updateSettings(settings);
    setSettings(res.data);
    initialVatThreshold.current = res.data?.[KEYS.VAT_THRESHOLD];
    return res.data;
  };

  const handleSave = async () => {
    if (vatThresholdChanged()) {
      setVatModalOpen(true);
      return;
    }
    setSaving(true);
    setToast(null);
    try {
      await persistSettings();
      setToast({ type: 'ok', text: 'Settings saved' });
      setTimeout(() => setToast(null), 2500);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Save failed';
      setToast({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleApplyToPending = async () => {
    setSaving(true);
    setToast(null);
    try {
      await persistSettings();
      const res = await recomputeVatThreshold();
      const r = res.data || {};
      setToast({
        type: 'ok',
        text: `Threshold saved. Re-evaluated ${r.evaluated ?? 0} invoices — ${
          r.newly_vat_eligible ?? 0
        } now VAT, ${r.no_longer_vat_eligible ?? 0} no longer VAT.`,
      });
      setVatModalOpen(false);
      setTimeout(() => setToast(null), 5000);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Save failed';
      setToast({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleOnlyGoingForward = async () => {
    setSaving(true);
    setToast(null);
    try {
      await persistSettings();
      setToast({ type: 'ok', text: 'Settings saved — applies to new invoices only.' });
      setVatModalOpen(false);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Save failed';
      setToast({ type: 'error', text: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelVatModal = () => {
    if (saving) return;
    setVatModalOpen(false);
  };

  const autoRouteEnabled = !!settings?.[KEYS.AUTO_ROUTE_ENABLED];

  return (
    <div
      style={{
        minHeight: '100vh',
        background: TOKENS.bg,
        fontFamily: UI_FONT,
        color: TOKENS.ink,
      }}
    >
      <NavHeader />
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '24px 24px 64px',
        }}
      >
        <h1
          style={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: '-0.02em',
            margin: '0 0 4px',
          }}
        >
          Settings
        </h1>
        <p
          style={{
            fontSize: 13,
            color: TOKENS.ink2,
            margin: '0 0 24px',
            lineHeight: 1.5,
          }}
        >
          Tune the routing and classification policy that the extraction
          pipeline applies to newly-uploaded invoices.
        </p>

        {loading ? (
          <div style={{ color: TOKENS.ink3 }}>Loading…</div>
        ) : (
          <>
            <Section title="Routing">
              <Field
                label="Auto-route high-confidence invoices to Pending Approval"
                helper="When enabled, invoices that classify with high confidence skip Under Review and land in Pending Approval. We never auto-approve — a human still has to act on the approval request."
              >
                <Checkbox
                  checked={autoRouteEnabled}
                  onChange={(v) => update(KEYS.AUTO_ROUTE_ENABLED, v)}
                />
              </Field>
              <Field
                label="Confidence threshold for auto-routing"
                helper="Minimum classification confidence required before an invoice is auto-routed to Pending Approval. Invoices at or above this score (and with a project + GL mapping) skip Under Review. Disabled when auto-routing is off."
                disabled={!autoRouteEnabled}
              >
                <NumberInput
                  value={settings[KEYS.AUTO_ROUTE_THRESHOLD]}
                  onChange={(v) => update(KEYS.AUTO_ROUTE_THRESHOLD, v)}
                  disabled={!autoRouteEnabled}
                />
              </Field>
            </Section>

            <Section title="Classification">
              <Field
                label="Assignment threshold"
                helper="Minimum confidence required before the classifier will attach a project to an invoice. Independent of routing."
              >
                <NumberInput
                  value={settings[KEYS.ASSIGNMENT_THRESHOLD]}
                  onChange={(v) => update(KEYS.ASSIGNMENT_THRESHOLD, v)}
                />
              </Field>
            </Section>

            <Section title="VAT">
              <Field
                label="VAT threshold (EUR)"
                helper="Invoices with a total above this amount enter the VAT workflow after approval. Set to 0 to route every VAT invoice (no threshold)."
              >
                <MoneyInput
                  value={settings[KEYS.VAT_THRESHOLD]}
                  onChange={(v) => update(KEYS.VAT_THRESHOLD, v)}
                />
              </Field>
              {/* Unlike the threshold above, this toggle has no retroactive
                  recompute — it only changes how batches are formed from here
                  on, so saving it never opens the "apply to pending" modal. */}
              <Field
                label="One VAT package per invoice (AP portal)"
                helper="When enabled, each VAT invoice gets its own package instead of being grouped with the vendor's other invoices for the period, and the wire request page is omitted (AP attaches the package to the portal and payment is initiated there). Applies to packages formed after the change — existing batches keep their current shape."
              >
                <Checkbox
                  checked={!!settings[KEYS.VAT_PER_INVOICE_PACKAGES]}
                  onChange={(v) => update(KEYS.VAT_PER_INVOICE_PACKAGES, v)}
                />
              </Field>
            </Section>

            <Section title="Display">
              <Field
                label="Show extracted text on invoice detail"
                helper="Toggles the raw extracted-text panel on the invoice detail page. Useful while validating extractor changes."
              >
                <Checkbox
                  checked={!!settings[KEYS.SHOW_EXTRACTED_TEXT]}
                  onChange={(v) => update(KEYS.SHOW_EXTRACTED_TEXT, v)}
                />
              </Field>
            </Section>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '8px 16px',
                  borderRadius: 6,
                  background: TOKENS.accent,
                  color: '#fff',
                  border: 'none',
                  fontFamily: UI_FONT,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: saving ? 'wait' : 'pointer',
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? 'Saving…' : 'Save settings'}
              </button>
              {toast && (
                <span
                  style={{
                    fontSize: 12.5,
                    color: toast.type === 'ok' ? TOKENS.ok : TOKENS.danger,
                  }}
                >
                  {toast.text}
                </span>
              )}
            </div>
          </>
        )}
      </div>
      {vatModalOpen && (
        <VatThresholdModal
          oldValue={initialVatThreshold.current}
          newValue={settings?.[KEYS.VAT_THRESHOLD]}
          busy={saving}
          onApply={handleApplyToPending}
          onForward={handleOnlyGoingForward}
          onCancel={handleCancelVatModal}
        />
      )}
    </div>
  );
}
